import React from 'react';

import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import PlayedCountFlag from './PlayedCountFlag';
import { getRoles, isSignedIn } from '../../utils/tokenUtils';

// The member profile page, where a signed-in member's flag goes.
const PROFILE_ROUTE = '/member/profile';

interface ExploreAccountButtonProps {
  // Bumped by Explore whenever the session changes (a sign-in through the setup drawer). The token
  // is still the single source of truth, this only forces the re-read, because nothing about
  // localStorage tells React to render again. Nothing reads the number: the changed prop is the
  // whole point of it.
  sessionVersion: number;
  // How many courses this member has ticked as played. Owned by Explore (useMyCourseFlags), not
  // refetched here, so the flag moves the instant a pin is ticked rather than on the next load.
  playedCount: number;
  onSignInClick: () => void;
}

// The control in the top bar: the played-count flag with a small down arrow beside it. The account
// drawer the arrow used to open was removed on 2026-10-23 (it only repeated links the profile
// already has, Sign out included). The arrow came back on 2026-11-04, the client's call: without
// it, nobody could tell the flag was a button. It is part of the same button, not a second control.
const ExploreAccountButton: React.FC<ExploreAccountButtonProps> = ({
  playedCount,
  onSignInClick,
}) => {
  const navigate = useNavigate();

  // Read on every render rather than held in state. sessionVersion is what makes that render
  // happen; keeping a second copy of "are they signed in" is how it goes stale.
  const signedIn = isSignedIn();

  // Signed out, it is the same flag, reading 0, rather than a "Sign in" button: the count a member
  // watches is also the question that brings a visitor in ("how many courses have you played?").
  // A tap opens MemberSetupDrawer, which asks exactly that.
  //
  // Signed in, the flag goes straight to the member's own page (the user's call, 2026-10-16): the
  // profile for a member, the admin dashboard for an admin (an admin who is also a member counts as
  // an admin here).
  const roles = signedIn ? getRoles() || [] : [];
  const home = roles.includes('Admin') ? '/admin/dashboard' : PROFILE_ROUTE;

  const label = !signedIn
    ? 'Sign in or create an account'
    : home === PROFILE_ROUTE
      ? 'Go to my profile'
      : 'Go to the admin dashboard';

  return (
    <div className="flex shrink-0 items-center">
      <button
        type="button"
        onClick={signedIn ? (): void => navigate(home) : onSignInClick}
        aria-haspopup={signedIn ? undefined : 'dialog'}
        aria-label={label}
        className="flex items-center gap-1 rounded-full py-1 pl-1 pr-1.5 transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <PlayedCountFlag count={signedIn ? playedCount : 0} className="h-8" />
        <ChevronDown
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-white/70"
        />
      </button>
    </div>
  );
};

export default ExploreAccountButton;
